import { useEffect, useState } from 'react';

import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { DateToShowMap } from '@/components/shared/components/SuperSearchBar/maps/DateToShowMap';
import { DirectionMap } from '@/components/shared/components/SuperSearchBar/maps/DirectionMap';
import { OrderMap } from '@/components/shared/components/SuperSearchBar/maps/OrderMap';
import { getDateSearchFilters } from '@/components/shared/components/SuperSearchBar/services/getDateSearchFilters';
import { getSearchResultsBySearchParams } from '@/components/shared/components/SuperSearchBar/services/getSearchResultsBySearchParams';
import { FilterByDate } from '@/components/shared/components/SuperSearchBar/types/FilterByDate';
import { FilterByType } from '@/components/shared/components/SuperSearchBar/types/FilterByType';
import { SearchBarFilterOptions } from '@/components/shared/components/SuperSearchBar/types/SearchBarFilterOptions';
import { SearchResult } from '@/components/shared/components/SuperSearchBar/types/SearchResult';
import { SortByType } from '@/components/shared/components/SuperSearchBar/types/SortByType';
import { Text } from '@/components/shared/components/Text';
import { useRoleListFilters } from '@/hooks/useRoleListFilters';
import { useUser } from '@/hooks/useUser';
import { queryStringBuilder } from '@/utils/queryStringBuilder';

export const useSuperSearchBar = () => {
  const { isUserAdmin } = useUser();

  const [searchBarState, setSearchBarState] = useState<SearchBarFilterOptions>({
    sortBy: SortByType.UPDATED_AT_DESCEND,
    filterByType: isUserAdmin ? FilterByType.APPLICATIONS : FilterByType.CHECKOUT_SESSIONS,
    filterByDate: FilterByDate.ALL_DATES,
    searchTerm: undefined,
    dateToShow: 'updatedAt',
  });

  const [pagination, setPagination] = useState({
    pageSize: 8,
    page: 0,
  });

  const [itemCount, setItemCount] = useState(0);

  const [requestLoading, setRequestLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[] | undefined>(undefined);

  const { searchTerm, sortBy, filterByDate, filterByType } = searchBarState;
  const { pageSize, page } = pagination;

  useEffect(() => {
    if (typeof isUserAdmin !== 'boolean') return;

    setSearchBarState((s) => ({
      ...s,
      filterByType: isUserAdmin ? FilterByType.APPLICATIONS : FilterByType.CHECKOUT_SESSIONS,
    }));
  }, [isUserAdmin]);

  useEffect(() => {
    if (searchTerm) {
      requestCriteriaByCurrentStateParams();
    }
    if (!searchTerm) {
      setSearchResults(undefined);
    }
  }, [searchBarState, pagination]);

  useEffect(() => {
    goToInitialPage();
  }, [searchTerm, sortBy, filterByDate, filterByType]);

  const requestCriteriaByCurrentStateParams = async () => {
    setRequestLoading(true);

    const queryString = queryStringBuilder({
      page: page + 1,
      pageSize,
      filters: baseQueryFilters.length > 0 ? baseQueryFilters.concat(dateSearchFilters) : dateSearchFilters,
      orderBy: OrderMap.fromSortBy(sortBy),
      direction: DirectionMap.fromSortBy(sortBy),
      search: searchTerm,
    });

    try {
      const { searchResults, itemCount } = await getSearchResultsBySearchParams(queryString, filterByType, pageSize);
      searchResults !== null && setSearchResults(searchResults);
      itemCount !== null && setItemCount(itemCount);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setRequestLoading(false);
    }
  };

  const sortByOptions: ActionsOption[] = Object.keys(SortByType).map((value) => ({
    display: <Text tId={value} />,
    onClick: () =>
      setSearchBarState((s) => ({
        ...s,
        sortBy: value as SortByType,
        dateToShow: DateToShowMap.fromSortBy(value as SortByType),
      })),
    currentSelection: value === sortBy,
  }));

  const filterByTypeOptions: ActionsOption[] = Object.values(FilterByType).map((value) => ({
    display: <Text tId={value} />,
    onClick: () => setSearchBarState((s) => ({ ...s, filterByType: value as FilterByType })),
  }));

  const filterByDateOptions: ActionsOption[] = Object.keys(FilterByDate).map((value) => ({
    display: <Text tId={value} />,
    onClick: () => setSearchBarState((s) => ({ ...s, filterByDate: value as FilterByDate })),
  }));

  const filtersByRole = useRoleListFilters();
  const baseQueryFilters = [...filtersByRole];
  const dateSearchFilters = getDateSearchFilters(filterByDate, sortBy);

  const goToInitialPage = () => {
    if (page === 0) return;
    setPagination((s) => ({ ...s, page: 0 }));
  };

  const handlePageChange = (_, newPage) => {
    setPagination((s) => ({ ...s, page: newPage }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((s) => ({ ...s, pageSize: parseInt(event.target.value, 10), page: 0 }));
  };

  const paginationProps = {
    page,
    itemCount,
    handlePageChange,
    pageSize,
    handlePageSizeChange,
  };

  return {
    searchBarState,
    setSearchBarState,
    searchResults,
    setSearchResults,
    sortByOptions,
    filterByTypeOptions,
    filterByDateOptions,
    requestLoading,
    paginationProps,
  };
};
